import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeedCardAdmin, HorizontalStoryFeedAdmin } from '../../components';
import {
  COLLAPSE_STORAGE_KEY,
  CollapseMode,
  CollapseSwitcher,
  CreateCardAdmin,
  FeedCardCollapsedLoader,
  FeedCardLoader,
  NewsFeedCommon,
  useAppSelector,
  useNewsFeed,
  usePinnedStoryId,
} from '../../shared';
import { NoContent } from '../Error';

import classes from './NewsFeedAdmin.module.scss';

export const NewsFeedAdmin: FunctionComponent = () => {
  const { t } = useTranslation();

  const { getPinnedStoryId } = usePinnedStoryId();

  const {
    getNewsFeedItems,
    loadMoreNewsFeedItems,
    getPinnedFeedItems,
    loadMorePinnedFeedItems,
    pinnedStorySection,
  } = useNewsFeed();

  const { newsFeed, isFetched: isFetchedNewsFeed } = useAppSelector(({ newsFeed }) => newsFeed);

  const [collapseMode, setCollapseMode] = useState(
    localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD
  );

  useEffect(() => {
    window.addEventListener('storage', () => {
      setCollapseMode(localStorage.getItem(COLLAPSE_STORAGE_KEY) || CollapseMode.STANDARD);
    });
  }, []);

  const pinnedStoryId = getPinnedStoryId(pinnedStorySection);

  useEffect(() => {
    if (pinnedStoryId) {
      getPinnedFeedItems(pinnedStoryId);
      return;
    }

    getNewsFeedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(() => {
    if (pinnedStoryId) {
      loadMorePinnedFeedItems(pinnedStoryId);
      return;
    }

    loadMoreNewsFeedItems();
  }, [loadMoreNewsFeedItems, loadMorePinnedFeedItems, pinnedStoryId]);

  const isCollapsed = useMemo(() => collapseMode === CollapseMode.COLLAPSED, [collapseMode]);

  const loader = useMemo(
    () => (isCollapsed ? <FeedCardCollapsedLoader /> : <FeedCardLoader />),
    [isCollapsed]
  );

  const hasCards = useMemo(() => Boolean(newsFeed.length), [newsFeed.length]);

  const content = useMemo(() => {
    if (!isFetchedNewsFeed) {
      return loader;
    }

    if (!hasCards) {
      return <NoContent />;
    }

    return (
      <NewsFeedCommon
        loader={loader}
        loadMore={loadMore}
        HorizontalStoryFeedComponent={HorizontalStoryFeedAdmin}
        CardComponent={FeedCardAdmin}
      />
    );
  }, [hasCards, isFetchedNewsFeed, loadMore, loader]);

  return (
    <div className={classes['container']}>
      <div className={classes['container__header']}>
        <h1 className={classes['container__header-title']}>{t('adminLayoutMenu.content')}</h1>
        <CollapseSwitcher
          storageKey={COLLAPSE_STORAGE_KEY}
          className={classes['container__header-switcher']}
        />
      </div>
      <CreateCardAdmin />
      {content}
    </div>
  );
};
